import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// GST Tagging admin (Stage 1, mig 144). admin/accounts/management. Tag the four item masters
// with an HSN code + default GST rate. "Tagged" = BOTH fields set. Whitelisted backend
// (/gst/*) can only touch hsn_code + default_gst_pct — never standard_rate.
const MASTERS = [['trims', 'Trims'], ['fabric', 'Fabric'], ['yarn', 'Yarn'], ['stock', 'General Store']];
const SLABS = ['0', '0.25', '3', '5', '12', '18', '28'];
const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
const inp = { padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const btn = { padding: '6px 12px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };

const isTagged = (r) => r.hsn_code != null && r.hsn_code !== '' && r.default_gst_pct != null && r.default_gst_pct !== '';

export default function GstTagging() {
  const [master, setMaster] = useState('trims');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [untaggedOnly, setUntaggedOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState(new Set());
  const [bulkHsn, setBulkHsn] = useState('');
  const [bulkGst, setBulkGst] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const loadCounts = useCallback(() => { api.get('/gst/counts').then(r => setCounts(r.data.data || {})).catch(() => {}); }, []);
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (untaggedOnly) p.set('untagged', '1');
    if (search.trim()) p.set('search', search.trim());
    api.get(`/gst/items/${master}?${p.toString()}`).then(r => { setRows((r.data.data || []).map(x => ({ ...x, _hsn: x.hsn_code || '', _gst: x.default_gst_pct != null ? String(Number(x.default_gst_pct)) : '' }))); setSel(new Set()); }).catch(() => {});
  }, [master, untaggedOnly, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const setField = (id, k, v) => setRows(rs => rs.map(r => r.id === id ? { ...r, [k]: v } : r));
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const saveRow = async (r) => {
    try {
      await api.patch(`/gst/tag/${master}/${r.id}`, { hsn_code: r._hsn || null, default_gst_pct: r._gst === '' ? null : Number(r._gst) });
      flash(`Saved ${r.name}`); loadCounts();
      if (untaggedOnly) setRows(rs => rs.filter(x => x.id !== r.id || !(r._hsn && r._gst)));  // drops out of the untagged list once fully tagged
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); }
  };
  const toggleSel = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkSet = async () => {
    // A BARE RETURN SAID NOTHING, AND THE BUTTON SAID NOTHING EITHER. `disabled={busy ||
    // !sel.size}` greyed it with no tooltip while this returned silently, so an operator who
    // had typed an HSN but not ticked a row had two layers of silence to guess past.
    if (!sel.size) { alert('Tick the rows to apply this to — nothing is selected.'); return; }
    if (!bulkHsn && !bulkGst) { alert('Enter an HSN and/or GST% to apply'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/gst/bulk/${master}`, { ids: [...sel], hsn_code: bulkHsn || null, default_gst_pct: bulkGst === '' ? null : Number(bulkGst) });
      flash(`Bulk-set ${r.data.data.updated} row(s)`); setBulkHsn(''); setBulkGst(''); load(); loadCounts();
    } catch (e) { alert(e.response?.data?.error || 'Bulk-set failed'); } finally { setBusy(false); }
  };

  const c = counts[master] || {};
  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px', color: '#1a1a2e' }}>🧾 GST Tagging</h2>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 14 }}>Tag item masters with an HSN code + default GST rate. Tagged = both fields set. Only these two fields are editable here.</div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 16 }}>
        {MASTERS.map(([k, label]) => { const mc = counts[k] || {}; return (
          <button key={k} onClick={() => { setMaster(k); }} style={{ padding: '9px 16px', border: 'none', borderBottom: master === k ? '3px solid #3B3A8F' : '3px solid transparent', background: 'transparent', color: master === k ? '#3B3A8F' : '#888', fontWeight: master === k ? 700 : 500, fontSize: 14, cursor: 'pointer' }}>
            {label} <span style={{ fontSize: 11, color: mc.tagged === mc.total ? '#166534' : '#B45309' }}>{mc.total != null ? `${mc.tagged}/${mc.total}` : ''}</span>
          </button>
        ); })}
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F' }}>{MASTERS.find(m => m[0] === master)[1]} — <span style={{ color: c.tagged === c.total ? '#166534' : '#B45309' }}>{c.tagged ?? '—'} of {c.total ?? '—'} tagged</span></div>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}><input type="checkbox" checked={untaggedOnly} onChange={e => setUntaggedOnly(e.target.checked)} /> Untagged only</label>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 search" style={{ ...inp, width: 200, marginLeft: 'auto' }} />
          {msg && <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>✓ {msg}</span>}
        </div>

        {/* Bulk-set bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#F7F6F3', borderRadius: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Bulk-set {sel.size} selected:</span>
          <input value={bulkHsn} onChange={e => setBulkHsn(e.target.value)} placeholder="HSN" maxLength={8} style={{ ...inp, width: 100 }} />
          <select value={bulkGst} onChange={e => setBulkGst(e.target.value)} style={{ ...inp, width: 90 }}><option value="">GST%</option>{SLABS.map(s => <option key={s} value={s}>{s}%</option>)}</select>
          <button disabled={busy} onClick={bulkSet} style={{ ...btn, opacity: busy ? 0.5 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>Apply to {sel.size}</button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, width: 28 }}><input type="checkbox" checked={rows.length > 0 && sel.size === rows.length} onChange={e => setSel(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())} /></th>
            {['Item', 'HSN', 'GST %', ''].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{untaggedOnly ? 'All tagged 🎉' : 'No items.'}</td></tr>}
            {rows.map(r => (
              <tr key={r.id} style={{ background: isTagged(r) ? '#f7fbf8' : '#fff' }}>
                <td style={td}><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} /></td>
                <td style={td}>{r.name}{r.extra && <span style={{ fontSize: 10, color: '#aaa', marginLeft: 6 }}>{r.extra}</span>}
                  {(r.suggested_hsn || r.suggested_gst != null) && <button type="button"
                    onClick={() => setRows(rs => rs.map(x => x.id === r.id ? { ...x, _hsn: x._hsn || (r.suggested_hsn || ''), _gst: x._gst || (r.suggested_gst != null ? String(r.suggested_gst) : '') } : x))}
                    title={`Trade-default SUGGESTION (confirm before saving): HSN ${r.suggested_hsn || '—'}, GST ${r.suggested_gst != null ? r.suggested_gst + '%' : '—'}`}
                    style={{ marginLeft: 8, fontSize: 10, background: '#FFF8E6', border: '1px solid #F0D98C', borderRadius: 4, color: '#8A6D00', cursor: 'pointer', padding: '1px 6px' }}>💡 suggest</button>}
                </td>
                <td style={td}><input value={r._hsn} onChange={e => setField(r.id, '_hsn', e.target.value)} maxLength={8} placeholder="4/6/8 digits" style={{ ...inp, width: 110 }} /></td>
                <td style={td}><select value={r._gst} onChange={e => setField(r.id, '_gst', e.target.value)} style={{ ...inp, width: 90 }}><option value="">—</option>{SLABS.map(s => <option key={s} value={s}>{s}%</option>)}</select></td>
                <td style={td}><button onClick={() => saveRow(r)} style={{ ...btn, background: '#166534', padding: '5px 12px' }}>Save</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
